/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { X, Save, Shield, Percent, DollarSign, TrendingDown, TrendingUp, AlertTriangle, Monitor, Clock, Power, List, Star } from 'lucide-react';
import { TradingAgentSettings, AgentRiskLevel } from '../hooks/use-trading-agent';

interface SettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentSettings: TradingAgentSettings;
    onSave: (newSettings: TradingAgentSettings) => void;
}

export const AiTradingAgentSettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, currentSettings, onSave }) => {
    const [settings, setSettings] = useState<TradingAgentSettings>(currentSettings);

    useEffect(() => {
        if (isOpen) {
            setSettings(currentSettings);
        }
    }, [currentSettings, isOpen]);

    if (!isOpen) return null;

    const handleSave = () => {
        onSave(settings);
        onClose();
    };

    const riskLevels: { level: AgentRiskLevel, desc: string, color: string }[] = [
        { level: 'Conservative', desc: 'Low freq, high confidence.', color: 'bg-blue-100 border-blue-200 text-blue-800' },
        { level: 'Balanced', desc: 'Moderate freq, balanced risk.', color: 'bg-green-100 border-green-200 text-green-800' },
        { level: 'Aggressive', desc: 'High freq, speculative.', color: 'bg-red-100 border-red-200 text-red-800' },
    ];

    const handleNumberChange = (field: keyof TradingAgentSettings, value: string) => {
        const num = parseFloat(value);
        if (!isNaN(num) || value === '') {
            setSettings(prev => ({ ...prev, [field]: value === '' ? 0 : num }));
        }
    };

    return (
        <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-stone-200 flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b border-stone-100 flex justify-between items-center shrink-0">
                    <h3 className="font-serif text-xl font-bold text-stone-900">Trading Agent & Widget</h3>
                    <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full text-stone-400 hover:text-stone-900" title="Close"><X size={20} /></button>
                </div>
                
                <div className="p-6 space-y-8 overflow-y-auto custom-scrollbar">
                    
                    {/* Widget Status Section */}
                    <div className="space-y-4">
                        <label className="text-xs font-bold text-stone-500 uppercase flex items-center gap-2"><Monitor size={14} /> Live Signal Widget</label>
                        <div className="bg-stone-50 p-4 rounded-xl border border-stone-200 space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-bold text-stone-700">Show Floating Widget</span>
                                <button 
                                    onClick={() => setSettings(prev => ({ ...prev, widgetEnabled: !prev.widgetEnabled }))}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.widgetEnabled ? 'bg-green-500' : 'bg-stone-300'}`}
                                >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.widgetEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>
                            
                            <div className="flex items-center justify-between border-t border-stone-200/50 pt-3">
                                <div className="flex items-center gap-2">
                                    <Clock size={16} className="text-stone-400" />
                                    <span className="text-sm text-stone-600">Refresh Rate (seconds)</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input 
                                        type="number" 
                                        value={settings.widgetRefreshInterval / 1000}
                                        onChange={(e) => handleNumberChange('widgetRefreshInterval', (Number(e.target.value) * 1000).toString())}
                                        className="w-16 p-1 text-right bg-white border border-stone-200 rounded-md text-sm font-mono"
                                        min="5"
                                        step="1"
                                    />
                                    <span className="text-xs text-stone-400">sec</span>
                                </div>
                            </div>

                            <div className="flex items-center justify-between border-t border-stone-200/50 pt-3">
                                <div className="flex items-center gap-2">
                                    <List size={16} className="text-stone-400" />
                                    <span className="text-sm text-stone-600">Max Signals to Show</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input 
                                        type="number" 
                                        value={settings.widgetMaxItems || 10}
                                        onChange={(e) => handleNumberChange('widgetMaxItems', e.target.value)}
                                        className="w-16 p-1 text-right bg-white border border-stone-200 rounded-md text-sm font-mono"
                                        min="1"
                                        max="50"
                                        step="1"
                                    />
                                    <span className="text-xs text-stone-400">items</span>
                                </div>
                            </div>

                            <div className="flex items-center justify-between border-t border-stone-200/50 pt-3">
                                <div className="flex items-center gap-2">
                                    <Star size={16} className="text-stone-400" />
                                    <span className="text-sm text-stone-600">Show Favorites Only</span>
                                </div>
                                <button 
                                    onClick={() => setSettings(prev => ({ ...prev, widgetFilterFavorites: !prev.widgetFilterFavorites }))}
                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings.widgetFilterFavorites ? 'bg-amber-400' : 'bg-stone-300'}`}
                                >
                                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${settings.widgetFilterFavorites ? 'translate-x-5' : 'translate-x-1'}`} />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="h-px bg-stone-100 w-full"></div>

                    {/* Risk Level */}
                    <div>
                        <label className="text-xs font-bold text-stone-500 uppercase flex items-center gap-2 mb-3"><Shield size={14} /> Risk Profile</label>
                        <div className="grid grid-cols-3 gap-2">
                            {riskLevels.map(rl => (
                                <button
                                    key={rl.level}
                                    onClick={() => setSettings(prev => ({ ...prev, riskLevel: rl.level }))}
                                    className={`p-3 rounded-lg border-2 text-center transition-all ${settings.riskLevel === rl.level ? `border-current scale-105 ${rl.color.replace('bg-', 'text-')}` : 'border-stone-200 bg-stone-50 hover:border-stone-300'}`}
                                >
                                    <span className={`font-bold text-xs ${settings.riskLevel === rl.level ? rl.color.replace('bg-', 'text-').replace('border-', 'text-') : 'text-stone-700'}`}>{rl.level}</span>
                                    <p className="text-[9px] text-stone-500 mt-1 leading-tight hidden sm:block">{rl.desc}</p>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Trade Size */}
                    <div>
                        <label className="text-xs font-bold text-stone-500 uppercase flex items-center gap-2 mb-2"><DollarSign size={14} /> Trade Size</label>
                        <div className="relative">
                            <input
                                type="number"
                                value={settings.tradeSize}
                                onChange={(e) => handleNumberChange('tradeSize', e.target.value)}
                                className="w-full p-3 pl-10 bg-stone-50 border border-stone-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-nobel-gold"
                            />
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 font-bold text-sm">SOL</span>
                        </div>
                         <p className="text-[10px] text-stone-500 mt-1">Amount of SOL to use for each trade signal.</p>
                    </div>

                    {/* Profit & Loss */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                             <label className="text-xs font-bold text-stone-500 uppercase flex items-center gap-2 mb-2"><TrendingUp size={14} /> Profit Target</label>
                             <div className="relative">
                                 <input
                                     type="number"
                                     value={settings.profitTarget}
                                     onChange={(e) => handleNumberChange('profitTarget', e.target.value)}
                                     className="w-full p-3 pr-8 bg-stone-50 border border-stone-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-nobel-gold"
                                 />
                                <Percent size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" />
                            </div>
                        </div>
                         <div>
                             <label className="text-xs font-bold text-stone-500 uppercase flex items-center gap-2 mb-2"><TrendingDown size={14} /> Max Drawdown</label>
                             <div className="relative">
                                 <input
                                     type="number"
                                     value={settings.dailyDrawdown}
                                     onChange={(e) => handleNumberChange('dailyDrawdown', e.target.value)}
                                     className="w-full p-3 pr-8 bg-stone-50 border border-stone-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-nobel-gold"
                                 />
                                <Percent size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" />
                            </div>
                        </div>
                    </div>

                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3 text-amber-800 text-xs">
                        <AlertTriangle size={24} className="shrink-0 mt-0.5" />
                        <div>
                            <strong>Disclaimer:</strong> The Auto-Trading Agent is an experimental feature. Use at your own risk. Past performance is not indicative of future results.
                        </div>
                    </div>
                </div>

                <div className="p-6 border-t border-stone-100 bg-stone-50 shrink-0">
                    <button onClick={handleSave} className="w-full py-3 bg-stone-900 text-white rounded-lg font-bold text-sm hover:bg-nobel-gold hover:text-stone-900 transition-colors flex items-center justify-center gap-2">
                        <Save size={16} /> Save Settings
                    </button>
                </div>
            </div>
        </div>
    );
};